import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Card } from 'react-bootstrap';
import { PivotGrid } from './PivotGrid';
import { usePivotQuery } from './usePivotQuery';
import { PivotFilterInput, PivotQueryVariables } from './graphql';
import { Dimension, PivotConfig, DropZone } from './PivotConfigurator';
import { getDatePresets, formatDateForInput } from './utils/datePresets';
import { COLLECTIONS_FLAT_SPECS, transformFlatSpec } from './fieldBundles';
import { PIVOT_LABELS, NULL_TOKEN, NULL_LABEL } from './constants';
import {
  DndContext,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
  closestCorners,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  type CollisionDetection,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { getPivotCatalog, getDisplayableDimensions } from './pivotCatalog';
import './pivot.css';
import { buildWhereFromFilterValues, Filters } from './filters';
import { endOfYear, startOfYear } from 'date-fns';
import { useRootStore } from 'store';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { FaTimesCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Zone bias collision detection for wide screens
const ZONES = new Set(['available', 'rows', 'cols']);

const collisionDetectionStrategy: CollisionDetection = (args) => {
  // 1) Prefer direct pointer intersections
  const pointer = pointerWithin(args);
  if (pointer.length) {
    // If the pointer is inside a zone container, prefer that
    const zoneHit = pointer.find((c) => ZONES.has(String(c.id)));
    if (zoneHit) return [zoneHit];
    return pointer;
  }
  // 2) Use closestCorners for smooth behavior
  return closestCorners(args);
};

// Simple vertical axis restriction modifier
const restrictToVerticalAxis = ({
  transform,
}: {
  transform: { x: number; y: number; scaleX: number; scaleY: number };
}) => ({
  ...transform,
  x: 0,
});

// ===============================
// Small Pane Components
// ===============================

interface AvailablePaneProps {
  className?: string;
  items: Dimension[];
  disabled?: boolean;
}

const AvailablePane: React.FC<AvailablePaneProps> = ({ className, items, disabled }) => (
  <div className={className}>
    <DropZone
      title={`Διαθέσιμα Πεδία (${items.length})`}
      dimensions={items}
      zone="available"
      className="pivot-zone-available"
      disabled={disabled}
    />
    <div className="pivot-keyboard-hint">
      Συμβουλή: Επιλέξτε ένα chip και πατήστε Space + ↑/↓ για αναδιάταξη
    </div>
  </div>
);

interface ColumnsPaneProps {
  className?: string;
  items: Dimension[];
  preview: Dimension[];
  disabled?: boolean;
}

const ColumnsPane: React.FC<ColumnsPaneProps> = ({ className, items, preview, disabled }) => (
  <div className={className}>
    {/* Column Preview */}
    <div className="pivot-columns-preview" hidden>
      {preview.length > 0 ? (
        preview.map((dim, i) => (
          <React.Fragment key={dim.id}>
            <div className="pivot-preview-token" title={`Move ${dim.label}`}>
              {dim.label}
            </div>
            {i < preview.length - 1 && <span className="pivot-preview-separator">▸</span>}
          </React.Fragment>
        ))
      ) : (
        <div className="pivot-preview-token" style={{ background: 'var(--text-muted)' }}>
          Σύνολα Στηλών
        </div>
      )}
    </div>
    <DropZone
      title={`Στήλες (${items.length})`}
      dimensions={items}
      zone="cols"
      className="pivot-zone-cols"
      disabled={disabled}
    />
  </div>
);

interface RowsPaneProps {
  className?: string;
  items: Dimension[];
  disabled?: boolean;
}

const RowsPane: React.FC<RowsPaneProps> = ({ className, items, disabled }) => (
  <div className={className}>
    <DropZone
      title={`Γραμμές (${items.length})`}
      dimensions={items}
      zone="rows"
      className="pivot-zone-rows"
      disabled={disabled}
    />
  </div>
);

interface GridPaneProps {
  className?: string;
  data?: any;
  loading?: boolean;
  error?: any;
  heatmapEnabled?: boolean;
  appliedBadges?: Array<{
    dimId: string;
    operator: string;
    values?: string[];
    label: string;
    text: string;
  }>;
  enabled: boolean;
}

/* **************************************************************************************
   Main
   ************************************************************************************** */

interface PivotDemoContentProps {
  className?: string;
  tableId?: string;
}

const PivotDemoContent = observer(
  ({ className = '', tableId = 'collections' }: PivotDemoContentProps) => {
    // Get catalog data for the specified table
    const catalog = getPivotCatalog(tableId);
    const location: any = useLocation();
    const { appStore } = useRootStore();

    const GridPane: React.FC<GridPaneProps> = ({
      className,
      data,
      loading,
      error,
      heatmapEnabled,
      appliedBadges,
      enabled,
    }) => (
      <div className={className}>
        {!enabled ? (
          <div className="pivot-demo-placeholder">
            <p>
              Προσθέστε διαστάσεις στις Γραμμές ή/καί τις Στήλες και μετά
              <br />
              πατήστε το κουμπί <strong>Αναζήτηση</strong> για να δείτε τον πίνακα pivot.
              <br />
              Π.χ. δοκιμάστε να σύρετε τον <strong>Συλλέκτη</strong> στις Γραμμές και το{' '}
              <strong>Έτος</strong> στις Στήλες.
            </p>
          </div>
        ) : showGrid ? (
          <PivotGrid
            data={data}
            loading={loading}
            error={error}
            heatmapEnabled={heatmapEnabled}
            appliedBadges={appliedBadges}
            tableId={tableId}
            rowDims={rowDims}
            colDims={colDims}
            measureId={measureId}
            filters={filters}
            filterValues={filterValues}
          />
        ) : (
          <div className="pivot-demo-placeholder">
            <p>Προσθέστε διαστάσεις στις Γραμμές ή Στήλες για να δείτε τον πίνακα pivot.</p>
            <p>
              Π.χ. δοκιμάστε να σύρετε τον <strong>Συλλέκτη</strong> στις Γραμμές και το{' '}
              <strong>Έτος</strong> στις Στήλες.
            </p>
          </div>
        )}
      </div>
    );

    const [config, setConfig] = useState<PivotConfig>({
      available: catalog.dimensions.map((d) => ({ id: d.id, label: d.label })),
      rows: [],
      cols: [],
    });

    const initialFilterValues = location?.state?.where ?? {
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date()),
    };
    const [filterValues, setFilterValues] = useState(initialFilterValues);

    const [measureId, setMeasureId] = useState(catalog.measures[0]?.id || 'count');

    const [where, setWhere] = useState<string>('{}');
    const [filters, setFilters] = useState<PivotFilterInput[]>([]);
    const [debouncedVars, setDebouncedVars] = useState<PivotQueryVariables | undefined>(undefined);
    const [activeDimension, setActiveDimension] = useState<Dimension | null>(null);
    const [density, setDensity] = useState<'comfort' | 'compact'>('compact');
    const [heatmapEnabled, setHeatmapEnabled] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [autoApply, setAutoApply] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState(false);

    // Name lookup maps for friendly badge display
    const [nameMaps, setNameMaps] = useState<{
      driverId: Map<string, string>;
      vehicleId: Map<string, string>;
      collectorId: Map<string, string>;
    }>({
      driverId: new Map(),
      vehicleId: new Map(),
      collectorId: new Map(),
    });

    // Response timing state
    const tStartRef = useRef<number | null>(null);
    const [lastResponseMs, setLastResponseMs] = useState<number | null>(null);

    // Debug modal refs and handlers
    const debugModalRef = useRef<HTMLDivElement>(null);
    const debugCloseButtonRef = useRef<HTMLButtonElement>(null);

    const copyToClipboard = async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        // Could add toast notification here
        console.log(`${label} copied to clipboard`);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    };

    // Memoize dimension arrays to avoid rebuilding on every render
    const rowDims = useMemo(() => config.rows.map((d) => d.id), [config.rows]);
    const colDims = useMemo(() => config.cols.map((d) => d.id), [config.cols]);

    const variables = useMemo(() => {
      return {
        table: tableId,
        rowDims,
        colDims,
        measureId,
        filters,
        where,
      };
    }, [tableId, rowDims, colDims, measureId, filters, where]);

    // Memoize zone lists for stable SortableContext during drags
    const availableItems = useMemo(() => config.available, [config.available]);
    const rowItems = useMemo(() => config.rows, [config.rows]);
    const colItems = useMemo(() => config.cols, [config.cols]);
    // Enables/disables the pivot query (used for filter changes)
    const [enabled, setEnabled] = useState<boolean>(false);
    // Normalize filters - sanitize null values and sort for stable cache keys
    const filtersNormalized = useMemo(() => {
      const cleaned = filters
        .map((f) => {
          // For null-safe operators, always use empty values array
          if (f.operator === 'notNull' || f.operator === 'isNull') {
            return { ...f, values: [] };
          }

          // For value-based operators, strip null/undefined/empty strings
          const vals = (f.values ?? []).filter((v) => v != null && v !== '');

          // If no valid values remain after filtering, omit the filter entirely
          if (
            ['eq', 'neq', 'in', 'nin', 'between', 'gte', 'lte'].includes(f.operator) &&
            vals.length === 0
          ) {
            return null;
          }

          return { ...f, values: vals };
        })
        .filter(Boolean) as PivotFilterInput[];

      // Return undefined if no filters remain
      if (cleaned.length === 0) return undefined;

      // Sort filters by (dimId, operator) for stable cache keys
      return cleaned.sort((a, b) => {
        const aCombined = a.dimId + a.operator;
        const bCombined = b.dimId + b.operator;
        return aCombined.localeCompare(bCombined);
      });
    }, [filters]);

    // Build query variables object with proper memoization
    const queryVars = useMemo<PivotQueryVariables>(
      () => ({
        table: tableId,
        rowDims,
        colDims,
        measureId,
        filters: filtersNormalized,
        where: where,
      }),
      [tableId, rowDims, colDims, measureId, filtersNormalized, where],
    );

    // Compute applied badges from filters for display
    const appliedBadges = useMemo(() => {
      if (!filtersNormalized || filtersNormalized.length === 0) return [];

      return filtersNormalized.map((filter) => {
        const dimension = catalog.dimensions.find((d) => d.id === filter.dimId);
        const label = dimension?.label ?? filter.dimId;

        let text = '';

        // Format text based on operator and values, with friendly names where possible
        switch (filter.operator) {
          case 'eq':
            const singleValue = String(filter.values?.[0] ?? '');
            // Look up friendly name if this is an ID-based filter
            if (filter.dimId === 'driverId') {
              text = nameMaps.driverId.get(singleValue) || singleValue;
            } else if (filter.dimId === 'vehicleId') {
              text = nameMaps.vehicleId.get(singleValue) || singleValue;
            } else if (filter.dimId === 'collectorId') {
              text = nameMaps.collectorId.get(singleValue) || singleValue;
            } else {
              text = singleValue;
            }
            break;
          case 'in':
            const multiValues = filter.values?.map(String) ?? [];
            // Look up friendly names for each value if this is an ID-based filter
            if (filter.dimId === 'driverId') {
              text = multiValues.map((v) => nameMaps.driverId.get(v) || v).join(', ');
            } else if (filter.dimId === 'vehicleId') {
              text = multiValues.map((v) => nameMaps.vehicleId.get(v) || v).join(', ');
            } else if (filter.dimId === 'collectorId') {
              text = multiValues.map((v) => nameMaps.collectorId.get(v) || v).join(', ');
            } else {
              text = multiValues.join(', ');
            }
            break;
          case 'between':
            if (filter.dimId === 'dateCollected' && filter.values?.length === 2) {
              const fromDate = new Date(filter.values[0]).toLocaleDateString('el-GR');
              const toDate = new Date(filter.values[1]).toLocaleDateString('el-GR');
              text = `${fromDate} - ${toDate}`;
            } else {
              text = filter.values?.map(String).join(' - ') ?? '';
            }
            break;
          case 'gte':
            if (filter.dimId === 'dateCollected' && filter.values?.[0]) {
              const fromDate = new Date(filter.values[0]).toLocaleDateString('el-GR');
              text = `Από: ${fromDate}`;
            } else {
              text = `≥ ${String(filter.values?.[0] ?? '')}`;
            }
            break;
          case 'lte':
            if (filter.dimId === 'dateCollected' && filter.values?.[0]) {
              const toDate = new Date(filter.values[0]).toLocaleDateString('el-GR');
              text = `Έως: ${toDate}`;
            } else {
              text = `≤ ${String(filter.values?.[0] ?? '')}`;
            }
            break;
          case 'isNull':
            text = '(null)';
            break;
          case 'notNull':
            text = 'όχι (null)';
            break;
          default:
            text = filter.values?.map(String).join(', ') ?? '';
        }

        return {
          dimId: filter.dimId,
          operator: filter.operator,
          values: filter.values?.map(String),
          label,
          text,
        };
      });
    }, [filtersNormalized, catalog.dimensions, nameMaps]);

    // Skip query when both dimensions are empty
    const skipQuery = rowDims.length === 0 && colDims.length === 0;
    const finalSkip = debouncedVars === undefined || skipQuery;

    // Hook to run the query with debounced variables
    const { data, loading, error, networkStatus, isBusy } = usePivotQuery({
      variables, // the memo above
      skip: !enabled, // ← query runs only after Search flips enabled=true
      isDragging,
    });

    // DnD sensors - always create with constant arity to avoid useEffect warnings
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: isBusy
          ? { distance: Number.POSITIVE_INFINITY } // When disabled, make activation impossible
          : {
              delay: 120,
              tolerance: 6,
            },
      }),
      // Always include keyboard sensor for accessibility
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Reset config when tableId changes
    useEffect(() => {
      const displayableDims = getDisplayableDimensions(tableId);
      setConfig({
        available: displayableDims.map((d) => ({ id: d.id, label: d.label })),
        rows: [],
        cols: [],
      });

      // Reset measure if current one isn't valid for new table
      const newCatalog = getPivotCatalog(tableId);
      const validMeasureIds = newCatalog.measures.map((m) => m.id);
      if (!validMeasureIds.includes(measureId)) {
        setMeasureId(newCatalog.measures[0]?.id || 'count');
      }
    }, [tableId, measureId]);

    // Debounce query variables to avoid excessive queries
    // useEffect(() => {
    //   const timer = setTimeout(() => {
    //     setDebouncedVars(queryVars);
    //   }, 150);

    //   return () => clearTimeout(timer);
    // }, [queryVars]);

    // Start timer on (debounced) query begin
    // useEffect(() => {
    //   if (!finalSkip) {
    //     tStartRef.current = performance.now();
    //     setLastResponseMs(null);
    //   }
    // }, [debouncedVars, finalSkip]);

    // Stop timer on settle
    useEffect(() => {
      if (finalSkip || loading || tStartRef.current == null) return;
      setLastResponseMs(Math.round(performance.now() - tStartRef.current));
      tStartRef.current = null;
    }, [loading, error, data, finalSkip]);

    // Handle escape key and focus trap for debug modal
    useEffect(() => {
      if (!showDebug) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowDebug(false);
          return;
        }

        // Focus trap
        if (e.key === 'Tab') {
          const modal = debugModalRef.current;
          if (!modal) return;

          const focusableElements = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      // Focus the close button when modal opens
      if (debugCloseButtonRef.current) {
        debugCloseButtonRef.current.focus();
      }

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showDebug]);

    // Auto-apply date filter with debounce
    useEffect(() => {
      if (!autoApply) return;

      const timer = setTimeout(() => {
        if (canApplyDateFilter) {
          applyDateFilter();
        }
      }, 300);

      return () => clearTimeout(timer);
    }, [dateFrom, dateTo, autoApply]);

    const handleConfigChange = (newConfig: PivotConfig) => setConfig(newConfig);

    const handleMeasureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      if (newValue !== measureId) {
        setMeasureId(newValue);
      }
    };

    const handleFiltersChange = (newFilters: PivotFilterInput[]) => {
      console.log('$$$ handleFiltersChange', newFilters);
      setFilters(newFilters);
    };

    const handleNameMapsChange = useCallback(
      (newNameMaps: {
        driverId: Map<string, string>;
        vehicleId: Map<string, string>;
        collectorId: Map<string, string>;
      }) => {
        setNameMaps(newNameMaps);
      },
      [],
    );

    const handleDensityToggle = (newDensity: 'comfort' | 'compact') => {
      if (newDensity !== density) {
        setDensity(newDensity);
      }
    };

    const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const preset = catalog.presets.find((p) => p.id === e.target.value);
      if (!preset) return;

      const rowDims = preset.rows
        .map((id) => catalog.dimensions.find((d) => d.id === id))
        .filter(Boolean)
        .map((d) => ({ id: d!.id, label: d!.label })) as Dimension[];

      const colDims = preset.cols
        .map((id) => catalog.dimensions.find((d) => d.id === id))
        .filter(Boolean)
        .map((d) => ({ id: d!.id, label: d!.label })) as Dimension[];

      const used = [...rowDims, ...colDims];
      const available = catalog.dimensions
        .filter((d) => !used.some((u) => u.id === d.id))
        .map((d) => ({ id: d.id, label: d.label }));

      handleConfigChange({ available, rows: rowDims, cols: colDims });
      e.target.value = '';
    };

    const handleFlatSpecChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const specEntry = COLLECTIONS_FLAT_SPECS[e.target.value];
      if (!specEntry) return;

      const availableDims = catalog.dimensions.map((d) => ({ id: d.id, label: d.label }));
      const {
        config: newConfig,
        filters: newFilters,
        dateRange,
      } = transformFlatSpec(specEntry.spec, availableDims);

      handleConfigChange(newConfig);
      handleFiltersChange(newFilters || []);

      // Set date range if provided in the spec
      if (dateRange) {
        setDateFrom(dateRange.from || '');
        setDateTo(dateRange.to || '');
      }

      e.target.value = '';
    };

    // Date range filter handlers
    const applyDateFilter = () => {
      // Remove any existing dateCollected filter
      const nonDateFilters = filters.filter((f) => f.dimId !== 'dateCollected');

      // Add new date filter if applicable
      let newFilters = [...nonDateFilters];

      if (dateFrom && dateTo) {
        // Both dates provided - use between
        newFilters.push({
          dimId: 'dateCollected',
          operator: 'between',
          values: [`${dateFrom}T00:00:00`, `${dateTo}T23:59:59`],
        });
      } else if (dateFrom) {
        // Only from provided - use gte
        newFilters.push({
          dimId: 'dateCollected',
          operator: 'gte',
          values: [`${dateFrom}T00:00:00`],
        });
      } else if (dateTo) {
        // Only to provided - use lte
        newFilters.push({
          dimId: 'dateCollected',
          operator: 'lte',
          values: [`${dateTo}T23:59:59`],
        });
      }

      handleFiltersChange(newFilters);
    };

    const clearDateFilter = () => {
      setDateFrom('');
      setDateTo('');

      // Remove any existing dateCollected filter
      const nonDateFilters = filters.filter((f) => f.dimId !== 'dateCollected');

      // Use undefined instead of empty array when no filters remain
      handleFiltersChange(nonDateFilters.length > 0 ? nonDateFilters : []);
    };

    // Date preset handler
    const applyDatePreset = (
      presetKey: 'last7' | 'last30' | 'last90' | 'thisMonth' | 'lastMonth',
    ) => {
      const presets = getDatePresets();
      const preset = presets[presetKey];

      const fromStr = formatDateForInput(preset.from);
      const toStr = formatDateForInput(preset.to);

      // Update inputs immediately
      setDateFrom(fromStr);
      setDateTo(toStr);

      // If auto-apply is on, trigger apply immediately
      if (autoApply) {
        const newFilters = [...filters.filter((f) => f.dimId !== 'dateCollected')];
        newFilters.push({
          dimId: 'dateCollected',
          operator: 'between',
          values: [`${fromStr}T00:00:00`, `${toStr}T23:59:59`],
        });
        handleFiltersChange(newFilters);
      }
    };

    // Validation for date range
    const dateRangeValid = !dateFrom || !dateTo || dateFrom <= dateTo;
    const canApplyDateFilter = dateRangeValid && Boolean(dateFrom || dateTo);

    const handleReset = () => {
      const displayableDims = getDisplayableDimensions(tableId);
      setConfig({
        available: displayableDims.map((d) => ({ id: d.id, label: d.label })),
        rows: [],
        cols: [],
      });
      setFilters([]);
      setDateFrom('');
      setDateTo('');
    };

    // Active drag state for drag overlay
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeZone, setActiveZone] = useState<null | 'rows' | 'cols' | 'available'>(null);
    const [overZone, setOverZone] = useState<null | 'rows' | 'cols' | 'available'>(null);

    // DnD handlers
    const handleDragStart = (event: DragStartEvent) => {
      // Guard against starting drag while busy
      if (isBusy) return;

      const data = event.active.data.current;
      setActiveId(event.active.id as string);
      if (data?.dimension) setActiveDimension(data.dimension);

      // Track active zone for axis locking
      const zone = data?.zone ?? null;
      setActiveZone(zone);
      setIsDragging(true);

      // Add global class to disable transforms during drag
      document.body.classList.add('pivot-dragging');
    };

    const handleDragOver = (event: any) => {
      // Guard against state mutations while busy
      if (isBusy) return;

      const overId = event.over?.id;
      if (!overId) {
        setOverZone(null);
        return;
      }

      const overIdStr = String(overId);

      // Check if it's a direct zone id
      if (overIdStr === 'available' || overIdStr === 'rows' || overIdStr === 'cols') {
        setOverZone(overIdStr);
      } else if (overIdStr.includes('-')) {
        // It's a chip id like "rows-region", extract the zone
        const [zone] = overIdStr.split('-', 2);
        if (zone === 'available' || zone === 'rows' || zone === 'cols') {
          setOverZone(zone);
        } else {
          setOverZone(null);
        }
      } else {
        setOverZone(null);
      }
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveDimension(null);
      setActiveZone(null);
      setOverZone(null);
      setIsDragging(false);
      setEnabled(false);
      // Remove global drag class
      document.body.classList.remove('pivot-dragging');

      // Guard against config changes while busy
      if (isBusy) return;

      if (!over) return;

      const activeData = active.data.current;
      if (!activeData?.dimension || !activeData?.zone) return;

      const sourceDim = activeData.dimension as Dimension;
      const sourceZone = activeData.zone as 'available' | 'rows' | 'cols';
      const overIdStr = String(over.id);

      let targetZone: 'available' | 'rows' | 'cols';
      let targetIndex: number;

      if (overIdStr === 'available' || overIdStr === 'rows' || overIdStr === 'cols') {
        targetZone = overIdStr;
        targetIndex = (config as any)[targetZone].length;
      } else if (overIdStr.includes('-')) {
        const [overZone, overDimId] = overIdStr.split('-', 2);
        targetZone = overZone as 'available' | 'rows' | 'cols';
        const overEl = document.querySelector<HTMLElement>(
          `.pivot-chip[data-sortable-id="${overIdStr}"]`,
        );
        targetIndex = (config as any)[targetZone].findIndex((d: Dimension) => d.id === overDimId);
        const activatorEvent = (event as any).activatorEvent as PointerEvent | undefined;
        if (overEl && activatorEvent && 'clientY' in activatorEvent) {
          const rect = overEl.getBoundingClientRect();
          const pointerY = activatorEvent.clientY;
          // If pointer Y is below the midline, insert after
          if (pointerY > rect.top + rect.height / 2) {
            targetIndex += 1;
          }
        }
        if (targetIndex === -1) targetIndex = (config as any)[targetZone].length;
      } else {
        return;
      }

      const newConfig: PivotConfig = { ...config };

      // Early exit for cross-zone no-op (dropping back to same position)
      if (sourceZone !== targetZone) {
        const srcArr = (config as any)[sourceZone];
        const srcIdx = srcArr.findIndex((d: Dimension) => d.id === sourceDim.id);
        const tgtArr = (config as any)[targetZone];

        // If moving to empty zone at index 0, and source was at index 0, it's potentially a no-op intent
        if (srcIdx === 0 && targetIndex === 0 && tgtArr.length === 0) {
          // Allow this move as it's changing zones
        }
      }

      // Remove from source
      const srcArr = [...(newConfig as any)[sourceZone]];
      const srcIdx = srcArr.findIndex((d: Dimension) => d.id === sourceDim.id);
      if (srcIdx >= 0) {
        srcArr.splice(srcIdx, 1);
        (newConfig as any)[sourceZone] = srcArr;
      }

      // Reorder/Insert
      if (sourceZone === targetZone && srcIdx >= 0) {
        let adjustedTarget = targetIndex;
        if (srcIdx < targetIndex) adjustedTarget--;

        // Guard against no-op moves
        if (srcIdx === adjustedTarget) {
          return;
        }

        const reordered = arrayMove((config as any)[sourceZone], srcIdx, adjustedTarget);
        (newConfig as any)[targetZone] = reordered as Dimension[];
      } else {
        const tgtArr = [...(newConfig as any)[targetZone]];
        tgtArr.splice(targetIndex, 0, sourceDim);
        (newConfig as any)[targetZone] = tgtArr;
      }

      // Check if config actually changed before updating (optimized comparison)
      const configChanged =
        newConfig.available.length !== config.available.length ||
        newConfig.rows.length !== config.rows.length ||
        newConfig.cols.length !== config.cols.length ||
        !newConfig.available.every((dim, i) => dim.id === config.available[i]?.id) ||
        !newConfig.rows.every((dim, i) => dim.id === config.rows[i]?.id) ||
        !newConfig.cols.every((dim, i) => dim.id === config.cols[i]?.id);

      if (configChanged) {
        handleConfigChange(newConfig);
      }
    };

    const handleHeatmapToggle = () => {
      setHeatmapEnabled(!heatmapEnabled);
    };

    const showGrid = config.rows.length > 0 || config.cols.length > 0;

    const { t, i18n } = useTranslation();

    /* **************************************************************************************
   Render Pivot
   ************************************************************************************** */
    return (
      <div className="pivot-page">
        {/* Screen Reader Announcements */}
        <div className="pivot-sr-only" aria-live="polite" id="pivot-announcements" />

        {/* Card A: Filters */}
        <div className="mb-3">
          <Filters
            onChange={(values) => {
              setFilterValues(values); // keep raw values
              setWhere(buildWhereFromFilterValues(values)); // returns a JSON string already
              setEnabled(true); // allow the query to run
            }}
            initialValues={filterValues}
          />
        </div>

        {/* Card B: Pivot Builder & Grid */}
        <Card className="shadow pivot-card">
          <Card.Header>
            <div className="pivot-card-title">Pivot Table Συλλογών</div>
            <div className="pivot-toolbar">
              <div className="pivot-measure-select">
                <label htmlFor="measure-select">Μέτρηση:</label>
                <select
                  id="measure-select"
                  value={measureId}
                  onChange={handleMeasureChange}
                  disabled={isBusy}
                >
                  {catalog.measures.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pivot-presets">
                <select onChange={handlePresetChange} defaultValue="" disabled={isBusy}>
                  <option value="">Γρήγορες Προεπιλογές...</option>
                  {catalog.presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hide Flat Specs in production to avoid competing preset systems */}
              {appStore.devMode && (
                <div className="pivot-flat-specs">
                  <select onChange={handleFlatSpecChange} defaultValue="" disabled={isBusy}>
                    <option value="">Flat Specs...</option>
                    {Object.entries(COLLECTIONS_FLAT_SPECS).map(([id, spec]) => (
                      <option key={id} value={id}>
                        {spec.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                type="button"
                className={heatmapEnabled ? 'active' : ''}
                onClick={handleHeatmapToggle}
                disabled={isBusy}
                aria-pressed={heatmapEnabled}
                hidden
              >
                Θερμικός χάρτης
              </button>

              <div className="pivot-density-toggle" hidden>
                <button
                  type="button"
                  className={density === 'comfort' ? 'active' : ''}
                  onClick={() => handleDensityToggle('comfort')}
                  disabled={isBusy}
                  aria-pressed={density === 'comfort'}
                >
                  Άνεση
                </button>
                <button
                  type="button"
                  className={density === 'compact' ? 'active' : ''}
                  onClick={() => handleDensityToggle('compact')}
                  disabled={isBusy}
                  aria-pressed={density === 'compact'}
                >
                  Συμπαγές
                </button>
              </div>

              <Button
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleReset();
                }}
                variant="outline-danger"
                size="sm"
              >
                <FaTimesCircle className="mb-1" />{' '}
                {i18n.language === 'gr' ? t('Καθάρισμα') : t('Clear')}
              </Button>

              {appStore.devMode && (
                <Button
                  type="button"
                  onClick={() => setShowDebug(true)}
                  // className="pivot-inspect-btn"
                  title="Έλεγχος μεταβλητών ερωτήματος και δεδομένων απόκρισης"
                  disabled={isBusy}
                  variant="dark"
                >
                  Έλεγχος Δεδομένων
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            <div
              className={`pivot-body ${density === 'compact' ? 'is-compact' : ''} ${
                isBusy ? 'pivot-dnd-disabled' : ''
              }`}
              data-busy={isBusy}
              lang="el"
            >
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                modifiers={
                  activeZone === overZone && (activeZone === 'rows' || activeZone === 'cols')
                    ? [restrictToVerticalAxis]
                    : undefined
                }
                measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <div className="pivot-layout">
                  <div className="pane-left">
                    <AvailablePane
                      className="area-fields"
                      items={availableItems}
                      disabled={isBusy}
                    />
                    <RowsPane className="area-rows" items={rowItems} disabled={isBusy} />
                  </div>

                  <div className="pane-right">
                    <ColumnsPane
                      className="area-cols"
                      items={colItems}
                      preview={config.cols}
                      disabled={isBusy}
                    />
                    <GridPane
                      className="area-table"
                      data={data?.pivot}
                      loading={loading}
                      error={error}
                      heatmapEnabled={heatmapEnabled}
                      appliedBadges={appliedBadges}
                      enabled={enabled}
                    />
                  </div>
                </div>

                {/* Drag Overlay for smooth drag preview */}
                <DragOverlay dropAnimation={null}>
                  {activeId ? (
                    <div className="pivot-drag-preview">
                      {(() => {
                        const rawId = activeId ?? '';
                        const dimId = rawId.includes('-') ? rawId.split('-')[1] : rawId;
                        return PIVOT_LABELS[dimId as keyof typeof PIVOT_LABELS] || dimId;
                      })()}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </Card.Body>
        </Card>

        {/* Debug Modal */}
        {showDebug && (
          <div
            ref={debugModalRef}
            className="pivot-debug-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="debug-modal-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDebug(false);
              }
            }}
          >
            <div className="pivot-debug-content">
              <div className="pivot-debug-header">
                <h3 id="debug-modal-title">Επιθεώρηση Ερωτήματος Pivot</h3>
                <div className="pivot-debug-meta">
                  <span className={`pivot-pill ${error ? 'err' : loading ? 'warn' : 'ok'}`}>
                    {error ? 'Σφάλμα' : loading ? 'Φόρτωση…' : 'Έτοιμο'}
                  </span>
                  <span className="pivot-kv">
                    Χρόνος απόκρισης: {lastResponseMs != null ? `${lastResponseMs} ms` : '—'}
                  </span>
                </div>
                <button
                  ref={debugCloseButtonRef}
                  type="button"
                  onClick={() => setShowDebug(false)}
                  className="pivot-debug-close"
                  aria-label="Κλείσιμο παραθύρου εντοπισμού σφαλμάτων"
                >
                  ✕
                </button>
              </div>
              <div className="pivot-debug-body">
                {finalSkip ? (
                  <div className="pivot-debug-skip">
                    <strong>Το ερώτημα παραλείφθηκε</strong> (χωρίς διαστάσεις γραμμών ή στηλών)
                  </div>
                ) : (
                  <div className="pivot-debug-grid">
                    <div className="pivot-debug-section">
                      <div className="pivot-debug-section-header">
                        <h4>Μεταβλητές Ερωτήματος</h4>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(
                              JSON.stringify(debouncedVars || queryVars, null, 2),
                              'Μεταβλητές',
                            )
                          }
                          className="pivot-debug-copy-btn"
                          title="Αντιγραφή μεταβλητών στο πρόχειρο"
                        >
                          📋 Αντιγραφή
                        </button>
                      </div>
                      <pre>{JSON.stringify(debouncedVars || queryVars, null, 2)}</pre>
                    </div>

                    <div className="pivot-debug-section">
                      <div className="pivot-debug-section-header">
                        <h4>Τοπική Κατάσταση</h4>
                        <button
                          type="button"
                          onClick={() => {
                            const state = {
                              tableId,
                              rowDims,
                              colDims,
                              measureId,
                              filters: filters.length > 0 ? filters : 'none',
                              heatmapEnabled,
                              density,
                              dateFrom: dateFrom || 'none',
                              dateTo: dateTo || 'none',
                              autoApply,
                              dateRangeValid,
                              skipFlags: { skipQuery, finalSkip },
                            };
                            copyToClipboard(JSON.stringify(state, null, 2), 'Τοπική Κατάσταση');
                          }}
                          className="pivot-debug-copy-btn"
                          title="Αντιγραφή κατάστασης στο πρόχειρο"
                        >
                          📋 Αντιγραφή
                        </button>
                      </div>
                      <pre>
                        {JSON.stringify(
                          {
                            tableId,
                            rowDims,
                            colDims,
                            measureId,
                            filters: filters.length > 0 ? filters : 'none',
                            heatmapEnabled,
                            density,
                            dateFrom: dateFrom || 'none',
                            dateTo: dateTo || 'none',
                            autoApply,
                            dateRangeValid,
                            skipFlags: { skipQuery, finalSkip },
                          },
                          null,
                          2,
                        )}
                      </pre>
                    </div>

                    <div className="pivot-debug-section">
                      <div className="pivot-debug-section-header">
                        <h4>Κατάσταση Ερωτήματος</h4>
                        <button
                          type="button"
                          onClick={() => {
                            const status = {
                              loading,
                              responseMs: lastResponseMs,
                              error: error?.message || null,
                            };
                            copyToClipboard(
                              JSON.stringify(status, null, 2),
                              'Κατάσταση Ερωτήματος',
                            );
                          }}
                          className="pivot-debug-copy-btn"
                          title="Αντιγραφή κατάστασης στο πρόχειρο"
                        >
                          📋 Αντιγραφή
                        </button>
                      </div>
                      <pre>
                        {JSON.stringify(
                          {
                            loading,
                            responseMs: lastResponseMs,
                            error:
                              process.env.NODE_ENV === 'development'
                                ? error?.message || null
                                : 'Κρυμμένο στην παραγωγή',
                          },
                          null,
                          2,
                        )}
                      </pre>
                    </div>

                    <div className="pivot-debug-section">
                      <div className="pivot-debug-section-header">
                        <h4>Σύνοψη Απόκρισης</h4>
                        {data?.pivot && (
                          <button
                            type="button"
                            onClick={() => {
                              const response = {
                                colTree: data.pivot.colTree?.length || 0,
                                rowLeaves: data.pivot.rowLeaves?.length || 0,
                                cells: data.pivot.cells?.length || 0,
                                totalCount: data.pivot.totalCount || 0,
                                firstRowLeaves: data.pivot.rowLeaves?.slice(0, 10),
                                firstCells: data.pivot.cells?.slice(0, 10),
                              };
                              copyToClipboard(
                                JSON.stringify(response, null, 2),
                                'Σύνοψη Απόκρισης',
                              );
                            }}
                            className="pivot-debug-copy-btn"
                            title="Αντιγραφή απόκρισης στο πρόχειρο"
                          >
                            📋 Αντιγραφή
                          </button>
                        )}
                      </div>
                      {data?.pivot ? (
                        <pre>
                          {JSON.stringify(
                            {
                              colTree: data.pivot.colTree?.length || 0,
                              rowLeaves: data.pivot.rowLeaves?.length || 0,
                              cells: data.pivot.cells?.length || 0,
                              totalCount: data.pivot.totalCount || 0,
                              firstRowLeaves: data.pivot.rowLeaves?.slice(0, 10),
                              firstCells: data.pivot.cells?.slice(0, 10),
                            },
                            null,
                            2,
                          )}
                        </pre>
                      ) : (
                        <div>Δεν υπάρχουν δεδομένα</div>
                      )}
                    </div>

                    {/* Key Matching Debug Section */}
                    {data?.pivot && (
                      <div className="pivot-debug-section">
                        <div className="pivot-debug-section-header">
                          <h4>Αντιστοίχιση Κλειδιών (Null Safety)</h4>
                          <button
                            type="button"
                            onClick={() => {
                              const colKeys =
                                data.pivot.colTree?.slice(0, 3).map((node) => node.key) || [];
                              const cellKeys =
                                data.pivot.cells?.slice(0, 3).map((cell) => cell.colKey) || [];
                              const keyMatch = {
                                colTreeKeys: colKeys,
                                cellKeys: cellKeys,
                                nullTokenUsed: NULL_TOKEN,
                                nullLabelUsed: NULL_LABEL,
                              };
                              copyToClipboard(
                                JSON.stringify(keyMatch, null, 2),
                                'Αντιστοίχιση Κλειδιών',
                              );
                            }}
                            className="pivot-debug-copy-btn"
                            title="Αντιγραφή αντιστοίχισης κλειδιών στο πρόχειρο"
                          >
                            📋 Αντιγραφή
                          </button>
                        </div>
                        <pre>
                          {JSON.stringify(
                            {
                              colTreeKeys:
                                data.pivot.colTree
                                  ?.slice(0, 3)
                                  .map((node) => ({ key: node.key, value: node.value })) || [],
                              cellKeys:
                                data.pivot.cells?.slice(0, 3).map((cell) => cell.colKey) || [],
                              nullTokenUsed: NULL_TOKEN,
                              nullLabelUsed: NULL_LABEL,
                              note: 'colTreeKeys και cellKeys πρέπει να ταιριάζουν ακριβώς',
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export interface PivotDemoProps {
  className?: string;
  tableId?: string;
}

export function CollectionsSrvPivotPage({ className, tableId = 'collections' }: PivotDemoProps) {
  // ✅ Use the global AppApolloProvider at app root. No nested ApolloProvider here.
  return <PivotDemoContent className={className} tableId={tableId} />;
}
